import React, { useState, useEffect } from "react";
import { AlertTriangle, AlertOctagon, Info } from "lucide-react";
import useAuth from "../hooks/useAuth";
import api from "../services/api";

// Color + icon for each threshold state, shared by banner and row badge.
const THRESHOLD_META = {
  "80":  { label: "80% of budget used",  color: "bg-yellow-50 text-yellow-700 border-yellow-200",  Icon: Info },
  "95":  { label: "95% of budget used",  color: "bg-orange-50 text-orange-700 border-orange-200",  Icon: AlertTriangle },
  "100": { label: "Budget cap reached",  color: "bg-red-50 text-red-700 border-red-200",          Icon: AlertOctagon },
};

export default function CostDashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [byCampaign, setByCampaign] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/costs/summary").catch(() => ({ data: {} })),
      api.get("/costs/by-campaign").catch(() => ({ data: { campaigns: [] } })),
    ]).then(([summaryRes, campaignRes]) => {
      setSummary(summaryRes.data);
      setByCampaign(campaignRes.data.campaigns || []);
    }).finally(() => setLoading(false));
  }, []);

  if (!user) return null;

  // Any campaign with a threshold_crossed is surfaced in the top banner.
  const breached = byCampaign.filter((c) => c.budget?.threshold_crossed);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Cost Dashboard</h1>

      {loading ? (
        <div className="text-center py-8 text-gray-400">Loading cost data...</div>
      ) : (
        <>
          {/* Budget breach banner */}
          {breached.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-red-200 overflow-hidden">
              <div className="px-4 py-3 bg-red-50 border-b border-red-200 flex items-center gap-2">
                <AlertOctagon className="w-4 h-4 text-red-600" />
                <span className="text-sm font-medium text-red-800">
                  {breached.length === 1
                    ? "1 campaign has crossed a budget threshold"
                    : `${breached.length} campaigns have crossed budget thresholds`}
                </span>
              </div>
              <div className="divide-y divide-gray-100">
                {breached.map((c) => {
                  const meta = THRESHOLD_META[c.budget.threshold_crossed];
                  const Icon = meta.Icon;
                  return (
                    <div key={c.campaign_id} className="px-4 py-3 flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${meta.color}`}>
                          <Icon className="w-3 h-3" />
                          {meta.label}
                        </span>
                        <span className="font-medium text-gray-900">{c.campaign_name}</span>
                        {c.budget.auto_paused && (
                          <span className="text-xs text-red-700 bg-red-100 px-1.5 py-0.5 rounded">Auto-paused</span>
                        )}
                      </div>
                      <div className="text-gray-600 font-mono text-xs">
                        ${c.total_cost.toFixed(2)} / ${c.budget.budget_cap?.toFixed(2)} ({Math.round(c.budget.ratio * 100)}%)
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              { label: "Total Spend", value: `$${(summary?.total_cost || 0).toFixed(2)}` },
              { label: "SMS Cost", value: `$${(summary?.sms_cost || 0).toFixed(2)}` },
              { label: "MMS Cost", value: `$${(summary?.mms_cost || 0).toFixed(2)}` },
              { label: "Messages", value: summary?.message_count || 0 },
            ].map((stat) => (
              <div key={stat.label} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <p className="text-sm font-medium text-gray-500">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
              </div>
            ))}
          </div>

          {/* By campaign table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900">Cost by Campaign</h2>
            </div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Campaign</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Messages</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Segments</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Cost/Msg</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Total Cost</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Budget</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {byCampaign.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center text-gray-400 text-sm">
                      No cost data yet
                    </td>
                  </tr>
                ) : (
                  byCampaign.map((row) => {
                    const crossed = row.budget?.threshold_crossed;
                    const meta = crossed && THRESHOLD_META[crossed];
                    return (
                      <tr key={row.campaign_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">{row.campaign_name}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 text-right">{row.message_count}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 text-right">{row.total_segments}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 text-right font-mono">
                          ${row.cost_per_contact?.toFixed(4) ?? "—"}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">
                          ${row.total_cost.toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm text-right">
                          {row.budget?.budget_cap == null ? (
                            <span className="text-xs text-gray-400">No cap</span>
                          ) : meta ? (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${meta.color}`}>
                              <meta.Icon className="w-3 h-3" />
                              {Math.round(row.budget.ratio * 100)}%
                            </span>
                          ) : (
                            <span className="text-xs text-gray-600">
                              {Math.round(row.budget.ratio * 100)}% of ${row.budget.budget_cap.toFixed(0)}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
